import { defineStore } from "pinia";
import { ref } from "vue";
import { v4 } from "uuid";
import type { ProviderEvent } from "./provider-event";
import type { AnyEvent } from "./any-event";
import type { InternalEvent } from "./internal-event";
import type { SuperProperties } from "./super-properties";

interface ProviderData {
  listeners: AnyEvent["name"][];
  sentEvents: ProviderEvent[];
}

const ANON_ID = "anon.id";

const createAnonymousId = (): string => {
  const id = v4();

  try {
    localStorage.setItem(ANON_ID, id);
    return id;
  } catch {
    return id;
  }
};

const getAnonymousIdOrCreate = (): string => {
  try {
    const id = localStorage.getItem(ANON_ID);
    return typeof id === "string" && id.length > 0 ? id : createAnonymousId();
  } catch {
    return createAnonymousId();
  }
};

export const useAnalyticsStore = defineStore("module:analytics", () => {
  const providers = ref<Record<string, ProviderData>>({});
  const eventsQueue = ref<AnyEvent[]>([]);
  const isOnHold = ref<boolean>(true);
  const firedEvents = ref<AnyEvent[]>([]);
  const anonymousId = ref<string>(getAnonymousIdOrCreate());
  const user =
    ref<Extract<InternalEvent, { name: "userIdentify" }>["payload"]>();
  const superProperties = ref<SuperProperties>({});

  const getNextQueuedEvent = (): AnyEvent | undefined => {
    return eventsQueue.value.shift();
  };

  const ensureProviderExists = (provider: string) => {
    if (!providers.value[provider]) {
      providers.value[provider] = { listeners: [], sentEvents: [] };
    }
  };

  const markEventAsSent = (provider: string, event: ProviderEvent) => {
    ensureProviderExists(provider);
    providers.value[provider].sentEvents.push(event);
  };

  const subscribeToEvent = (provider: string, eventName: AnyEvent["name"]) => {
    ensureProviderExists(provider);

    if (!providers.value[provider].listeners.includes(eventName)) {
      providers.value[provider].listeners.push(eventName);
    }
  };

  return {
    providers,
    eventsQueue,
    isOnHold,
    firedEvents,
    markEventAsSent,
    subscribeToEvent,
    getNextQueuedEvent,
    user,
    superProperties,
    anonymousId,
  };
});
